import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import {
  IconAtom01,
  IconExpand01,
  IconLogOut01,
  IconMinimize01,
  IconShuffle01,
  IconUser01,
  IconBell01,
} from "@pentabd/icons";
import { Avatar, Badge, Button, Dropdown } from "@pentabd/ui";

import { ExtraRoleModal } from "@components";

import { useAuth } from "@hooks/useAuth";
import { useUser } from "@hooks/useUser";

import { GetEmployeeDetails } from "@utils/getEmployeeDetails";
import { removeStorage } from "@utils/local-store";

import { ROUTES } from "@constants/routes";

import { useEmployeeDetailsWithRefetch } from "@hooks/HRM/employee/useEmployee";
import { getLogo } from "../Logo";
import "./style.scss";

import { useNotifications } from "@hooks/notification/useNotifications";
import { useKeycloak } from "@react-keycloak/web";

import { socket, socket as socketInstance } from "@utils/socket";

export const CustomHeader = ({
  layoutState,
  setLayoutState,
  refetchHeaderState,
}: {
  layoutState: string;
  setLayoutState: any;
  refetchHeaderState: boolean;
}): JSX.Element => {
  const navigate = useNavigate();
  const { login, logout } = useAuth();

  const [availableRoles, setAvailableRoles] = useState<
    { tempAccMasterId: number; email: string; position: string; type: string }[]
  >([]);

  const [currentRole, setCurrentRole] = useState<{
    tempAccMasterId: number;
    email: string;
    position: string;
  } | null>(null);
  const [switchRoleModalOpen, setSwitchRoleModalOpen] =
    useState<boolean>(false);

  const [showRedDot, setShowRedDot] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");

  const { t } = useTranslation();

  const { user } = useUser();
  const { employeeMasterId, preferred_username } = user || {};
  const { employeeInfo, handleQuery } =
    useEmployeeDetailsWithRefetch(employeeMasterId);
  const {
    email,
    positionName: position,
    tempApprovedAccount,
    employeeCode,
  } = employeeInfo || {};

  //token
  const { keycloak } = useKeycloak();
  const token = keycloak.token as string;

  const { employeeName, employeeImage } = GetEmployeeDetails(employeeInfo);

  const { notifications } = useNotifications({
    isRead: false,
  });

  const handleToggle = () => {
    if (layoutState === "layout__with_sidebar") {
      setLayoutState("layout__without_sidebar");
    } else setLayoutState("layout__with_sidebar");
  };

  const onSwitchRoleOpen = () => {
    setSwitchRoleModalOpen(true);
  };

  const onSwitchRoleClose = () => {
    setSwitchRoleModalOpen(false);
  };

  const handleNotificationToast = () => {
    setShowRedDot(false);
    setNotificationMessage("");
    navigate(ROUTES.NOTIFICATION.NOTIFICATIONS);
  };

  useEffect(() => {
    const socket = socketInstance(employeeCode, token);
    socket.auth = { username: employeeCode };

    socket.on("connect", () => {
      // console.log("Socket Connected", socket.connected);
      if (socket.connected) {
        // console.log("Listening on channel " + employeeCode);

        socket.on(employeeCode, (message: any) => {
          // console.log("Message on channel " + channel + " arrived!");
          const msg = JSON.parse(message);
          const filteredMessage = msg.split('"')[1];
          setNotificationMessage(filteredMessage);
        });

        socket.on("disconnect", function () {
          console.log("disconnected");
          setNotificationMessage("");
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [employeeCode, token]);

  useEffect(() => {
    const newTemAccountEmails =
      tempApprovedAccount && tempApprovedAccount.length > 0
        ? [
            {
              id: "parent_account",
              email,
              position,
            },
            ...tempApprovedAccount,
          ]
        : [
            {
              id: 1,
              email,
              position,
            },
          ];

    const roles = newTemAccountEmails.filter(
      (item: { tempAccMasterId: number; email: string; position: string }) =>
        item.email?.toLowerCase() !== preferred_username,
    );

    const findCurrentRole = newTemAccountEmails.find(
      (item: any) => item?.email?.toLowerCase() === preferred_username,
    );

    setCurrentRole(findCurrentRole || null);
    setAvailableRoles(roles || []);
  }, [preferred_username, employeeInfo]);

  useEffect(() => {
    handleQuery();
  }, [refetchHeaderState]);

  useEffect(() => {
    if (notificationMessage) {
      setShowRedDot(true);
    }
    const messageTimeout = setTimeout(() => {
      setNotificationMessage("");
    }, 10000);

    return () => {
      clearTimeout(messageTimeout);
    };
  }, [notificationMessage]);

  useEffect(() => {
    if (!notifications?.data?.length) {
      setShowRedDot(false);
    } else {
      setShowRedDot(true);
    }
  }, [notifications]);

  return (
    <>
      <div className="px-10 py-5 custom_header bg-info">
        <div className=" d-flex align-center gap-8">
          {getLogo().logoWhite}
          <Button onClick={() => handleToggle()} className="p-0 p-4">
            {layoutState === "layout__with_sidebar" ? (
              <IconExpand01 size="24" />
            ) : (
              <IconMinimize01 size="24" />
            )}
          </Button>
        </div>
        <div className="d-flex align-items-center">
          <div className="me-8 position-relative">
            <Link
              to={ROUTES.NOTIFICATION.NOTIFICATIONS}
              className="position-relative"
              onClick={() => {
                setShowRedDot(false);
                setNotificationMessage("");
              }}
            >
              <IconBell01 size="24" fill="white" />
              {showRedDot && <span className="notification-alert"></span>}
            </Link>
            {notificationMessage && (
              <div
                className="notification-message"
                onClick={handleNotificationToast}
              >
                {notificationMessage}
              </div>
            )}
          </div>
          <div className="me-8">
            <Badge
              label={currentRole?.position}
              type="info"
              size="sm"
              className="text-nowrap w-max"
            />
          </div>
          <div className="text-dark custom_header_avatar">
            <Dropdown
              buttonType="customIcon"
              portal={true}
              customIcon={
                <Avatar
                  imageURL={employeeImage}
                  name={employeeName}
                  size="xs"
                />
              }
              info={{
                email: email,
                name: employeeName,
              }}
              listItem={[
                {
                  icon: <IconUser01 fill="primary" size="17" />,
                  name: t("HEADER.DROPDOWN.VIEW_PROFILE"),
                  onClick: () => {
                    navigate(ROUTES.HRM.PROFILE);
                  },
                },
                {
                  icon: <IconAtom01 fill="primary" size="17" />,
                  name: t("HEADER.DROPDOWN.SWITCH_ROLE"),
                  onClick: () => {
                    onSwitchRoleOpen();
                  },
                  hide: availableRoles.length > 0 ? false : true,
                },
                {
                  hr: true,
                  icon: <IconShuffle01 fill="primary" size="17" />,
                  name: t("HEADER.DROPDOWN.UPDATE_PASSWORD"),
                  onClick: () => {
                    navigate(ROUTES.HRM.PROFILE);
                    login({ action: "UPDATE_PASSWORD" });
                  },
                },
                {
                  icon: <IconLogOut01 fill="primary" size="17" />,
                  name: t("HEADER.DROPDOWN.SIGN_OUT"),
                  onClick: () => {
                    removeStorage("module");
                    logout({ redirectUri: window.location.origin });
                  },
                },
              ]}
            />
          </div>
        </div>
      </div>

      <ExtraRoleModal
        isModalOpen={switchRoleModalOpen}
        onCloseAction={onSwitchRoleClose}
        roles={availableRoles}
      />
    </>
  );
};
